import { buildApiUrl } from '../config';

/**
 * Calendar Service - API client for calendar events
 */

/**
 * Fetch calendar events with filters
 * @param {Object} options - Query options
 * @param {string} options.start - Start date ISO string
 * @param {string} options.end - End date ISO string
 * @param {string} options.chamber - Chamber filter ('house', 'senate', or undefined for all)
 * @param {string} options.eventType - Event type filter
 * @param {string} options.body - Legislative body ID
 * @param {string} options.committee - Committee ID
 * @param {AbortSignal} options.signal - Abort signal for cancellation
 * @returns {Promise<Object>} Events data with total count
 */
export const fetchCalendarEvents = async ({
  start,
  end,
  chamber,
  eventType,
  body,
  committee,
  signal
} = {}) => {
  const params = new URLSearchParams();
  
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  if (chamber && chamber !== 'all') params.set('chamber', chamber);
  if (eventType && eventType !== 'all') params.set('event_type', eventType);
  if (body && body !== 'all') params.set('body', body);
  if (committee && committee !== 'all') params.set('committee', committee);

  const url = buildApiUrl(`/api/calendar?${params.toString()}`);
  
  const response = await fetch(url, { signal });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch calendar events: ${response.status}`);
  }
  
  return response.json();
};

/**
 * Fetch a single event by ID
 * @param {string} eventId - Event UUID
 * @param {AbortSignal} signal - Abort signal
 * @returns {Promise<Object>} Event with related bills
 */
export const fetchEventById = async (eventId, signal) => {
  const url = buildApiUrl(`/api/calendar/${eventId}`);
  
  const response = await fetch(url, { signal });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch event: ${response.status}`);
  }
  
  return response.json();
};

/**
 * Fetch events for a specific bill
 * @param {string} billId - Bill UUID
 * @param {AbortSignal} signal - Abort signal
 * @returns {Promise<Object>} Events linked to bill
 */
export const fetchEventsByBill = async (billId, signal) => {
  const url = buildApiUrl(`/api/calendar/bill/${billId}`);
  
  const response = await fetch(url, { signal });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch bill events: ${response.status}`);
  }
  
  return response.json();
};

/**
 * Get upcoming events for a specific chamber
 * @param {string} chamber - 'house' or 'senate'
 * @param {number} limit - Max events to return
 * @param {AbortSignal} signal - Abort signal
 * @returns {Promise<Object>} Upcoming events
 */
export const fetchUpcomingEvents = async (chamber, limit = 10, signal) => {
  const params = new URLSearchParams({
    start: new Date().toISOString(),
    limit: limit.toString()
  });
  
  if (chamber && chamber !== 'all') {
    params.set('chamber', chamber);
  }
  
  const url = buildApiUrl(`/api/calendar?${params.toString()}`);
  
  const response = await fetch(url, { signal });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch upcoming events: ${response.status}`);
  }
  
  return response.json();
};

export default {
  fetchCalendarEvents,
  fetchEventById,
  fetchEventsByBill,
  fetchUpcomingEvents
};
